<script setup lang="ts">
import { useCVSectionTranslations } from '~/composables/useCVSectionTranslations';
import { isRTLLanguage } from '~/constants/cvLanguages';
import { getLinkDisplayName, getLinkMeta, resolveLinkType } from '~/constants/linkTypes';
import type { CVData, Education as OldEducation, Experience as OldExperience } from '~/types';
import type { Education as CvEducation, Experience as CvExperience } from '~/types/cv';
import { formatDateOfBirth } from '~/utils/dateHelpers';

// Lokální typy pro časové sekce, zarovnané s daty z DB
type TimelineExperience = {
  company?: string;
  title?: string;
  startDate?: string;
  endDate?: string;
  period?: string;
  description?: string;
}

type TimelineEducation = {
  school: string;
  degree?: string;
  startDate?: string;
  endDate?: string;
  period?: string;
}

const props = defineProps<{
  cvData: CVData;
  experiencesList: Array<TimelineExperience | CvExperience | OldExperience>;
  educationList: Array<TimelineEducation | CvEducation | OldEducation>;
  showQRCode?: boolean;
  /** Free accounts get the logo pattern over the whole card, as the PDF does. */
}>();

// Get CV section translations based on cvLanguage
const cvLanguage = computed(() => props.cvData?.cvLanguage || 'en')
const { translations: sectionTranslations } = useCVSectionTranslations(cvLanguage)
const isRTL = computed(() => isRTLLanguage(cvLanguage.value))
const { t } = useI18n()

// Levels, dates and the word for "present" read the way the PDF reads them
const { skillLevel, languageLevel, languagePercent, formatPeriod, formatCertificateDate } = useWebCv(
  () => props.cvData,
  () => ({ present: sectionTranslations.value.present || t('cv.edit.present'), nativeSpeaker: sectionTranslations.value.nativeSpeaker }),
)

const certificates = computed(() => (props.cvData?.certificates ?? []).filter(cert => cert && (cert.name || cert.issuer)))
const hobbies = computed(() => (props.cvData?.hobbies ?? []).filter(hobby => hobby && hobby.trim()))

// Computed properties for better data handling
const topSkills = computed(() => {
  // Only use regular skills for expertise, not technical skills
  const regular = props.cvData?.skills;

  if (regular && Array.isArray(regular) && regular.length > 0) {
    return regular;
  }

  return [];
});

// Total skills count including all skill types
const totalSkillsCount = computed(() => {
  let count = 0;

  // Regular skills
  if (props.cvData?.skills && Array.isArray(props.cvData.skills)) {
    count += props.cvData.skills.length;
  }

  // Technical skills
  if (props.cvData?.technicalSkills && Array.isArray(props.cvData.technicalSkills)) {
    count += props.cvData.technicalSkills.length;
  }

  // Soft skills
  if (props.cvData?.softSkills && Array.isArray(props.cvData.softSkills)) {
    count += props.cvData.softSkills.length;
  }

  return count;
});

const { publicCvLinkPath } = usePublicCvLinks();
const contactInfo = computed<{ email?: string; phone?: string; website: string }>(() => {
  const contact = (props.cvData?.contact ?? {}) as Partial<NonNullable<CVData['contact']>>;
  // Replace website with current public card URL
  const route = useRoute();
  const path = publicCvLinkPath('card', String(route.params.id));
  const publicCardUrl = import.meta.client ? `${window.location.origin}${path}` : path;

  return {
    email: contact.email,
    phone: contact.phone,
    website: publicCardUrl,
  };
});

// Every link with a URL; platform icon and label come from the shared link-type map
const socialLinks = computed(() => (props.cvData?.websites ?? []).filter(site => site?.url));

const dateOfBirth = computed(() => formatDateOfBirth(props.cvData?.contact?.dateOfBirth, cvLanguage.value));

// Collapsible sections state
const isExperienceExpanded = ref(true);
const isEducationExpanded = ref(true);

// Utility functions
const getSocialIcon = (site: { name: string; url: string; type?: CVData['websites'][number]['type'] }) => getLinkMeta(site).biClass;
const getSocialName = (site: { name: string; url: string; type?: CVData['websites'][number]['type'] }) =>
  resolveLinkType(site) === 'other' ? site.name || site.url : getLinkDisplayName(site);

const getExperienceRole = (exp: TimelineExperience | CvExperience | OldExperience): string => exp?.title ?? exp?.position ?? ''

// Download contact function
const downloadContact = () => {
  if (!props.cvData) return;

  const contact = props.cvData.contact;
  const name = props.cvData.name || 'Contact';

  // Create vCard format
  const vcard = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${name}`,
    contact?.email ? `EMAIL:${contact.email}` : '',
    contact?.phone ? `TEL:${contact.phone}` : '',
    contact?.website ? `URL:${contactInfo.value.website}` : '',
    contact?.linkedin ? `URL;TYPE=LinkedIn:${contact.linkedin}` : '',
    contact?.location ? `ADR;TYPE=HOME:;;;${contact.location.replace(/[;\n]/g, ' ')};;;` : '',
    /^\d{4}-\d{2}-\d{2}$/.test(contact?.dateOfBirth || '') ? `BDAY:${contact!.dateOfBirth}` : '',
    props.cvData.title ? `TITLE:${props.cvData.title}` : '',
    props.cvData.aboutMe ? `NOTE:${props.cvData.aboutMe}` : '',
    'END:VCARD'
  ].filter(line => line && line !== '').join('\n');

  // Create download
  const blob = new Blob([vcard], { type: 'text/vcard' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name.replace(/\s+/g, '_')}.vcf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
</script>

<template>
  <div v-if="props.cvData" :dir="isRTL ? 'rtl' : 'ltr'" class="mx-auto w-full max-w-screen-xl">
    <!-- Business Card Layout - Flat Design -->
    <div class="relative overflow-hidden rounded-2xl bg-white shadow-lg dark:bg-gray-900">

      <!-- Simple background -->
      <div class="absolute inset-0  dark:bg-gray-900" />

      <!-- Main Business Card Content -->
      <div class="relative z-10 min-h-[80vh]">
        <div class="w-full p-4 md:p-8">
          <div class="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-5">

            <!-- Left Section - Personal Brand (5 columns) -->
            <div class="text-white lg:col-span-5">

              <!-- Profile Photo - Circular Design -->
              <div v-if="props.cvData?.photoUrl" class="relative mb-6 flex justify-center lg:justify-start">
                <div class="relative">
                  <div
                    class="size-32 overflow-hidden rounded-full border-2 border-gray-300 lg:size-40 dark:border-gray-700">
                    <img
:src="props.cvData.photoUrl" :alt="props.cvData?.name || 'Profile'"
                      class="size-full object-cover">
                  </div>
                </div>
              </div>

              <!-- Name & Title - Flat Design -->
              <div class="mb-8 text-center lg:text-left">
                <h1 class="text-4xl font-bold leading-tight text-white lg:text-5xl" :class="{ 'text-right': isRTL }">
                  {{ props.cvData?.name || 'Your Name' }}
                </h1>
                <h2 class="!pointer-events-none mb-3 mt-[-10px] !text-base !font-medium !text-green-500 dark:!text-green-500" :class="{ 'text-right': isRTL }">
                  {{ props.cvData?.title || 'Your Job Title' }}
                </h2>
                <!-- Simple accent line -->
                <div class="mx-auto h-0.5 w-16 bg-primary lg:mx-0" :class="{ 'ml-auto': isRTL }" />
              </div>

              <!-- Contact Information - Simple List -->
              <div v-if="contactInfo?.email || contactInfo?.phone || contactInfo?.website" id="contact" class="mb-6">
                <h3 class="mb-3 flex items-center text-base font-semibold text-white" :class="{ 'text-right': isRTL }">
                  <div class="mr-2 flex size-6 items-center justify-center rounded-lg bg-primary" :class="{ 'ml-3': isRTL }">
                    <i class="bi bi-chat-dots-fill text-xs text-white" />
                  </div>
                  {{ sectionTranslations.contact }}
                </h3>

                <div class="space-y-2 text-sm">
                  <div v-if="contactInfo?.email" class="flex items-center">
                    <i class="bi bi-envelope-fill mr-3 w-4 text-primary" :class="{ 'ml-3': isRTL }" />
                    <a
:href="'mailto:' + contactInfo.email"
                      class="truncate text-primary transition-colors hover:text-black dark:text-white dark:hover:text-primary">
                      {{ contactInfo.email }}
                    </a>
                  </div>

                  <div v-if="contactInfo?.phone" class="flex items-center">
                    <i class="bi bi-phone-fill mr-3 w-4 text-primary" :class="{ 'ml-3': isRTL }" />
                    <a
:href="'tel:' + contactInfo.phone"
                      class=" !text-primary transition-colors hover:!text-black dark:!text-white dark:hover:!text-primary">
                      {{ contactInfo.phone }}
                    </a>
                  </div>

                  <div v-if="contactInfo?.website" class="flex items-center">
                    <i class="bi bi-globe mr-3 w-4 text-purple-400" :class="{ 'ml-3': isRTL }" />
                    <a
:href="contactInfo.website" target="_blank"
                      class="truncate  text-primary transition-colors hover:text-black dark:text-white dark:hover:text-primary">
                      {{ t('publicPage.businessCard') }}
                    </a>
                  </div>

                  <div v-if="props.cvData.contact?.website" class="flex items-center">
                    <i class="bi bi-globe2 mr-3 w-4 text-blue-400" :class="{ 'ml-3': isRTL }" />
                    <a
:href="props.cvData.contact.website" target="_blank"
                      class="truncate text-primary transition-colors hover:text-black dark:text-white dark:hover:text-primary">
                      {{ props.cvData.contact.website }}
                    </a>
                  </div>

                  <div v-if="props.cvData.contact?.linkedin" class="flex items-center">
                    <i class="bi bi-linkedin mr-3 w-4 text-blue-400" :class="{ 'ml-3': isRTL }" />
                    <a
:href="props.cvData.contact.linkedin" target="_blank"
                      class="truncate text-primary transition-colors hover:text-black dark:text-white dark:hover:text-primary">
                      LinkedIn
                    </a>
                  </div>

                  <div v-if="props.cvData.contact?.location" class="flex items-center">
                    <i class="bi bi-geo-alt-fill mr-3 w-4 text-primary" :class="{ 'ml-3': isRTL }" />
                    <span class="truncate text-primary dark:text-white">{{ props.cvData.contact.location }}</span>
                  </div>

                  <div v-if="dateOfBirth" class="flex items-center">
                    <i class="bi bi-calendar3 mr-3 w-4 text-primary" :class="{ 'ml-3': isRTL }" />
                    <span class="truncate text-primary dark:text-white">
                      <span class="sr-only">{{ sectionTranslations.dateOfBirth }}: </span>{{ dateOfBirth }}
                    </span>
                  </div>

                  <div v-if="props.cvData.drivingLicense" class="flex items-center">
                    <i class="bi bi-car-front-fill mr-3 w-4 text-primary" :class="{ 'ml-3': isRTL }" />
                    <span class="truncate text-primary dark:text-white">{{ sectionTranslations.drivingLicense }}: {{ props.cvData.drivingLicense }}</span>
                  </div>
                </div>
              </div>

              <!-- About Me - Flat Card -->
              <div v-if="props.cvData?.aboutMe" id="about" class="mb-6">
                <div class="rounded-xl border border-gray-100 px-3 py-4 dark:border-gray-700 dark:bg-gray-800">
                  <div class="flex items-center">
                    <h3 class="flex items-center text-base font-semibold text-white">
                      {{ sectionTranslations.aboutMe }}
                    </h3>
                  </div>
                  <p class="whitespace-pre-line !text-sm leading-relaxed dark:!text-gray-300" :class="{ 'text-right': isRTL }">{{ props.cvData.aboutMe }}</p>
                </div>
              </div>

              <!-- Hard Skills Section -->
              <div v-if="props.cvData?.technicalSkills?.length" id="hard-skills" class="mb-6">
                <div class="mb-3 flex items-center">
                  <h3 class="flex items-center text-base font-semibold text-white">
                    <div class="mr-2 flex size-6 items-center justify-center rounded-lg bg-blue-500" :class="{ 'ml-3': isRTL }">
                      <i class="bi bi-gear-fill text-xs text-white" />
                    </div>
                   {{ sectionTranslations.technicalSkills }}  ({{ props.cvData.technicalSkills.length }})
                  </h3>
                </div>

                <!-- Hard Skills Tags -->
                <div class="flex flex-wrap gap-0">
                  <div
v-for="skill in props.cvData.technicalSkills" :key="skill"
                    class="mb-1 mr-1 inline-flex items-center gap-2 rounded-md border border-blue-500/30 bg-blue-500/20 px-3 py-1 !text-xs font-medium !text-blue-700 dark:!text-blue-100">
                    {{ skill }}<CVLevelDots :level="skillLevel(skill)" class="!text-blue-600 dark:!text-blue-200" />
                  </div>
                </div>
              </div>

              <!-- Soft Skills Section -->
              <div v-if="props.cvData?.softSkills?.length" id="soft-skills" class="mb-6">
                <div class="mb-3 flex items-center">
                  <h3 class="flex items-center text-base font-semibold text-white" :class="{ 'text-right': isRTL }">
                    <div class="mr-2 flex size-6 items-center justify-center rounded-lg bg-green-500 " :class="{ 'ml-3': isRTL }">
                      <i class="bi bi-heart-fill text-xs text-white" />
                    </div>
                    {{ sectionTranslations.softSkills }} ({{ props.cvData.softSkills.length }})
                  </h3>
                </div>

                <!-- Soft Skills Tags -->
                <div class="flex flex-wrap gap-0">
                  <div
v-for="skill in props.cvData.softSkills" :key="skill"
                    class="mb-1 mr-1 inline-flex items-center gap-2 rounded-md border border-green-500/30 bg-green-500/20 px-3 py-1 !text-xs font-medium !text-green-700 dark:!text-green-100">
                    {{ skill }}<CVLevelDots :level="skillLevel(skill)" class="!text-green-600 dark:!text-green-200" />
                  </div>
                </div>
              </div>

              <!-- Languages Section -->
              <div v-if="props.cvData?.languages?.length" id="languages" class="mb-6">
                <div class="mb-3 flex items-center">
                  <h3 class="flex items-center text-base font-semibold text-white" :class="{ 'text-right': isRTL }">
                    <div class="mr-2 flex size-6 items-center justify-center rounded-lg bg-indigo-500" :class="{ 'ml-3': isRTL }">
                      <i class="bi bi-translate text-xs text-white" />
                    </div>
                    {{ sectionTranslations.languages }}
                  </h3>
                </div>

                <div class="space-y-2">
                  <div v-for="language in props.cvData.languages" :key="language.name">
                    <div
                      class="inline-block rounded-md border border-indigo-500/30 bg-indigo-500/20 px-2 py-1 text-xs text-indigo-400" :class="{ 'text-right': isRTL }"><div
                        class="font-bold text-indigo-400"> {{ language.name
                        }} </div><template v-if="language.level"> - {{ languageLevel(language.level) }}</template>
                    </div>
                    <!-- With levels on, and only for a level we can read -->
                    <div v-if="languagePercent(language.level) > 0" class="mt-1.5 h-1 max-w-[12rem] overflow-hidden rounded-full bg-indigo-500/20" aria-hidden="true">
                      <div class="h-full rounded-full bg-indigo-500" :style="{ width: `${languagePercent(language.level)}%` }" />
                    </div>
                  </div>
                </div>
              </div>
              <!-- QR Code / Download Section -->
              <div v-if="contactInfo?.website && showQRCode" class="mb-6">
                <div class="mb-3 flex items-center">
                  <h3 class="flex items-center text-base font-semibold text-white" :class="{ 'text-right': isRTL }">
                    <div class="mr-2 flex size-6 items-center justify-center rounded-lg bg-purple-500" :class="{ 'ml-3': isRTL }">
                      <i class="bi bi-download block text-xs text-white" />
                    </div>
                    {{ sectionTranslations.scanToSaveContact }}
                  </h3>
                </div>

                <!-- Desktop: QR Code -->
                <div class="hidden rounded-xl text-center md:block ">
                  <div class=" flex w-full justify-center">
                    <div class="w-full max-w-[200px] rounded-lg">
                      <CommonQRCode
:link="contactInfo.website" :size="160" background-color="#FFFFFF"
                        foreground-color="#000000" />
                    </div>
                  </div>
                </div>

                <!-- Mobile: Download Button -->
                <div class="mt-3 block">
                  <button
                    class="flex w-full items-center justify-center space-x-3 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 py-2 !font-medium text-white transition-all duration-200 hover:from-purple-600 hover:to-blue-600"
                    :class="{ 'flex-row-reverse': isRTL }"
                    @click="downloadContact">
                    <i class="bi bi-download text-lg" />
                    <div class="text-sm">{{ sectionTranslations.scanToSaveContact }}</div>
                  </button>
                </div>
              </div>
            </div>

            <!-- Right Section - Skills & Portfolio (7 columns) -->
            <div id="skills" class="lg:col-span-7">

              <!-- Skills Matrix - Flat (Only show if has general skills) -->
              <div v-if="topSkills.length" class="mb-6">
                <div class="mb-4 flex items-center">
                  <h3 class="flex items-center text-xl font-bold text-white" :class="{ 'text-right': isRTL }">
                    <div class="mr-3 flex size-8 items-center justify-center rounded-lg bg-yellow-500" :class="{ 'ml-3': isRTL }">
                      <i class="bi bi-lightning-fill text-sm text-white" />
                    </div>
                    {{ sectionTranslations.skills }}
                  </h3>
                </div>

                <!-- Flat Skills Grid -->
                <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div
v-for="skill in topSkills" :key="skill"
                    class="rounded-lg border border-gray-100 bg-gray-800 p-3 dark:border-gray-700">
                    <!-- The skill and, with levels on, its dots (no bar that claims a level nobody set) -->
                    <div class="flex items-center justify-between gap-3">
                      <h4 class="text-sm font-medium !text-white">{{ skill }}</h4>
                      <CVLevelDots :level="skillLevel(skill)" class="!text-yellow-500" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Stats Grid - Flat -->
              <div class="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                <!-- Experience count -->
                <div class="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4 text-center dark:bg-gray-800">
                  <div class="mb-1 text-4xl font-bold text-blue-600 dark:text-blue-400">{{ props.experiencesList?.length
                    || 0 }}</div>
                  <div class="text-sm font-bold text-blue-400 dark:text-blue-200">{{ sectionTranslations.experiences }}</div>
                </div>

                <!-- Education count -->
                <div class="rounded-lg border border-purple-500/30 bg-purple-500/10 p-4 text-center dark:bg-gray-800">
                  <div class="mb-1 text-4xl font-bold text-purple-600 dark:text-purple-400">{{
                    props.educationList?.length || 0 }}</div>
                  <div class="text-sm font-bold text-purple-400 dark:text-purple-200">{{ sectionTranslations.education }}</div>
                </div>

                <!-- Skills count -->
                <div class="rounded-lg border border-green-500/30 bg-green-500/10 p-4 text-center dark:bg-gray-800">
                  <div class="mb-1 text-4xl font-bold text-green-600 dark:text-green-400">{{ totalSkillsCount }}</div>
                  <div class="text-sm font-bold text-green-500 dark:text-green-200">{{ sectionTranslations.skills }}</div>
                </div>

                <!-- Languages count -->
                <div class="rounded-lg border border-gray-500/30 bg-gray-500/10 p-2 py-4 text-center dark:bg-gray-800">
                  <div class="mb-1 text-4xl font-bold text-gray-600 dark:text-gray-400">{{
                    props.cvData?.languages?.length || 0 }}</div>
                  <div class="text-sm font-bold text-gray-400 dark:text-gray-200">{{ sectionTranslations.languages }}</div>
                </div>
              </div>

              <!-- Work Experience Section - Collapsible -->
              <div v-if="props.experiencesList?.length" id="experience" class="mb-6">
                <button
                  class="mb-4 flex h-14 w-full cursor-pointer !items-center justify-between rounded-lg p-2 transition-all hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  @click="isExperienceExpanded = !isExperienceExpanded">
                  <h3 class="flex !items-center pt-4 text-xl font-bold text-white" :class="{ 'text-right': isRTL }" >
                    <div class="mr-2 flex size-8 items-center justify-center rounded-lg bg-blue-500" :class="{ 'ml-3': isRTL }">
                      <i class="bi bi-briefcase-fill text-sm text-white" />
                    </div>
                    {{ sectionTranslations.experiences }} ({{ props.experiencesList.length }})
                  </h3>
                  <div
                    class="flex size-8 items-center justify-center rounded-lg bg-blue-500/20 text-blue-400 transition-all">
                    <i :class="isExperienceExpanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="text-sm" />
                  </div>
                </button>

                <!-- Experience List - Collapsible -->
                <div v-show="isExperienceExpanded" class="space-y-3 transition-all duration-300 ease-in-out">
              <div
v-for="(exp, index) in props.experiencesList" :key="index"
                    class="rounded-lg border border-gray-100 p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  <h4 class="font-bold text-white">{{ exp.company || getExperienceRole(exp) }} </h4>
                  <div v-if="exp.startDate || exp.period" class="mt-1 text-xs font-medium text-blue-400 lg:mt-0">
                    {{ formatPeriod(exp) }}
                      </div>
                    </div>
                <div v-if="getExperienceRole(exp)" class="flex !text-sm font-semibold dark:text-white">{{ getExperienceRole(exp) }}</div>

                <div v-if="exp.description" class="mt-2 whitespace-pre-line !text-sm leading-relaxed dark:!text-gray-300">{{ exp.description }}</div>
                  </div>
                </div>
              </div>

              <!-- Education Section - Collapsible -->
              <div v-if="props.educationList?.length" id="education" class="mb-6">
                <button
                  class="mb-4 flex h-14 w-full cursor-pointer !items-center justify-between rounded-lg p-2 transition-all hover:bg-purple-500/10 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                  @click="isEducationExpanded = !isEducationExpanded">
                  <h3 class="flex items-center pt-4 text-xl font-bold text-white">
                    <div class="mr-3 flex size-8 items-center justify-center rounded-lg bg-purple-500" :class="{ 'ml-3': isRTL }">
                      <i class="bi bi-mortarboard-fill text-sm text-white" />
                    </div>
                    {{ sectionTranslations.education }} ({{ props.educationList.length }})
                  </h3>
                  <div
                    class="flex size-8 items-center justify-center rounded-lg bg-purple-500/20 text-purple-400 transition-all">
                    <i :class="isEducationExpanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="text-sm" />
                  </div>
                </button>

                <!-- Education List - Collapsible -->
                <div v-show="isEducationExpanded" class="space-y-3 transition-all duration-300 ease-in-out">
              <div
v-for="(edu, index) in props.educationList" :key="index"
                    class="rounded-lg border border-gray-100 p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div class="mb-2 flex flex-col lg:flex-row lg:items-start lg:justify-between">
                      <h4 class="!text-base font-bold text-white">{{ edu.degree }}</h4>
                  <div v-if="edu.startDate || edu.period" class="mt-1 text-xs font-medium text-purple-400 lg:mt-0">
                    {{ formatPeriod(edu) }}
                      </div>
                    </div>

                    <div class="mb-2">
                      <p class="!text-sm font-semibold text-primary">{{ edu.school }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Certificates -->
              <div v-if="certificates.length" id="certificates" class="mb-6">
                <h3 class="mb-4 flex items-center text-xl font-bold text-white" :class="{ 'text-right': isRTL }">
                  <div class="mr-3 flex size-8 items-center justify-center rounded-lg bg-teal-500" :class="{ 'ml-3': isRTL }">
                    <i class="bi bi-patch-check-fill text-sm text-white" />
                  </div>
                  {{ sectionTranslations.certificates }}
                </h3>
                <div class="space-y-3">
                  <div v-for="(cert, index) in certificates" :key="index" class="rounded-lg border border-gray-100 p-4 dark:border-gray-700 dark:bg-gray-800" :class="{ 'text-right': isRTL }">
                    <a v-if="cert.url" :href="cert.url" target="_blank" rel="noopener noreferrer" class="font-bold text-teal-500 hover:underline">{{ cert.name }}</a>
                    <h4 v-else class="font-bold text-white">{{ cert.name }}</h4>
                    <p v-if="cert.issuer || cert.date" class="mt-1 !text-sm text-primary dark:!text-gray-300">
                      {{ [cert.issuer, formatCertificateDate(cert.date)].filter(Boolean).join(' · ') }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Hobbies -->
              <div v-if="hobbies.length" id="hobbies" class="mb-6">
                <h3 class="mb-4 flex items-center text-xl font-bold text-white" :class="{ 'text-right': isRTL }">
                  <div class="mr-3 flex size-8 items-center justify-center rounded-lg bg-pink-500" :class="{ 'ml-3': isRTL }">
                    <i class="bi bi-stars text-sm text-white" />
                  </div>
                  {{ sectionTranslations.hobbies }}
                </h3>
                <div class="flex flex-wrap gap-2" :class="{ 'justify-end': isRTL }">
                  <span v-for="(hobby, index) in hobbies" :key="index" class="rounded-md border border-pink-500/30 bg-pink-500/20 px-3 py-1 !text-xs font-medium !text-pink-700 dark:!text-pink-100">{{ hobby }}</span>
                </div>
              </div>

              <!-- Custom Sections -->
              <div
                v-for="(section, sectionIndex) in props.cvData?.customSections"
                :key="'custom-' + sectionIndex"
                v-show="section.sectionTitle && section.items?.length"
                class="mb-6"
              >
                <div class="mb-4 flex items-center">
                  <h3 class="flex items-center text-xl font-bold text-white" :class="{ 'text-right': isRTL }">
                    <div class="mr-3 flex size-8 items-center justify-center rounded-lg bg-orange-500" :class="{ 'ml-3': isRTL }">
                      <i class="bi bi-grid-fill text-sm text-white" />
                    </div>
                    {{ section.sectionTitle }}
                  </h3>
                </div>

                <div class="space-y-3">
                  <div
                    v-for="(item, index) in section.items"
                    :key="index"
                    class="rounded-lg border border-gray-100 p-4 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div class="flex gap-4" :class="{ 'flex-row-reverse': isRTL }">
                      <div v-if="item.image" class="size-16 flex-shrink-0 overflow-hidden rounded-lg">
                        <img :src="item.image" :alt="item.title" class="size-full object-cover">
                      </div>
                      <div class="flex-1">
                        <a
                          v-if="item.url"
                          :href="item.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="font-bold text-orange-400 hover:underline"
                          :class="{ 'text-right': isRTL }"
                        >
                          {{ item.title }}
                        </a>
                        <h4 v-else class="font-bold text-white" :class="{ 'text-right': isRTL }">{{ item.title }}</h4>
                        <p v-if="item.text" class="mt-1 whitespace-pre-line !text-sm leading-relaxed dark:!text-gray-300" :class="{ 'text-right': isRTL }">{{ item.text }}</p>
                        <p v-if="item.url" class="mt-1 break-all !text-xs text-gray-500" :class="{ 'text-right': isRTL }">
                          {{ item.url }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Portfolio & Links Section -->
              <div class="space-y-6">

                <!-- Social Networks - Flat Style -->
                <div v-if="socialLinks.length">
                  <h4 class="mb-3 flex items-center text-base font-semibold text-white">
                    <div class="mr-2 flex size-6 items-center justify-center rounded-lg bg-green-700" :class="{ 'ml-3': isRTL }">
                      <i class="bi bi-share-fill text-xs text-white" />
                    </div>
                    {{ sectionTranslations.connectWithMe }}
                  </h4>
                  <div class="grid grid-cols-2 gap-2 lg:grid-cols-4">
                    <a
v-for="link in socialLinks" :key="link.url" :href="link.url" target="_blank"
                      class="rounded-lg border border-gray-300 bg-gray-200 p-3 text-center transition-colors hover:border-green-500/50 dark:border-gray-700 dark:bg-gray-800">
                      <div class="mx-auto mb-2 flex size-8 items-center justify-center rounded-lg bg-green-700 " :class="{ 'ml-3': isRTL }">
                        <i :class="getSocialIcon(link)" class="text-sm text-white" />
                      </div>
                      <p class="truncate text-xs font-medium text-white">{{ getSocialName(link) }}</p>
                    </a>
                  </div>
                </div>




              </div>
            </div>
          </div>


        </div>
      </div>
    </div>
  </div>


  <!-- No Data State -->
  <div v-else class="rounded border border-gray-300 bg-gray-100 p-4 text-center dark:border-gray-700">
    <i class="bi bi-exclamation-triangle mb-2 text-2xl text-gray-500" />
    <h3 class="font-bold text-gray-700">{{ sectionTranslations.noDataAvailable }}</h3>
    <p class="text-sm text-gray-600">{{ sectionTranslations.noDataDescription }}</p>
  </div>
</template>

<style scoped>
/* Advanced animations for pixel-perfect experience */
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-6px);
  }
}

@keyframes glow {

  0%,
  100% {
    opacity: 0.5;
  }

  50% {
    opacity: 1;
  }
}

@keyframes slideInScale {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Glass morphism effect */
.backdrop-blur-xl {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(55, 65, 81, 0.3);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(to bottom, #3B82F6, #8B5CF6);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(to bottom, #2563EB, #7C3AED);
}

/* Hover effects */
.group:hover .group-hover\:scale-105 {
  transform: scale(1.05);
}

.group:hover .group-hover\:scale-110 {
  transform: scale(1.1);
}

.group:hover .group-hover\:-translate-y-2 {
  transform: translateY(-8px);
}

/* Advanced text effects */
.bg-clip-text {
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Custom gradient borders */
.gradient-border {
  position: relative;
}

.gradient-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  background: linear-gradient(45deg, #3B82F6, #8B5CF6, #EC4899);
  border-radius: inherit;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

/* Skills progress bar enhancement */
.skill-progress {
  position: relative;
  overflow: hidden;
}

.skill-progress::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent);
  transform: translateX(-100%);
  animation: shimmer 2s infinite;
}

/* Contact cards premium styling */
.contact-card {
  position: relative;
  overflow: hidden;
}

.contact-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent);
  transition: left 0.6s ease;
}

.contact-card:hover::before {
  left: 100%;
}

/* Photo glow effect */
.photo-glow {
  position: relative;
}

.photo-glow::after {
  content: '';
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, #3B82F6, #8B5CF6, #EC4899, #F59E0B);
  border-radius: inherit;
  z-index: -1;
  filter: blur(8px);
  animation: glow 3s ease-in-out infinite;
}

/* Typography enhancements */
.text-shadow {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* Social icons hover animation */
.social-icon {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.social-icon:hover {
  animation: float 1s ease-in-out infinite;
}

/* Stats cards enhancement */
.stat-card {
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg,
      transparent,
      rgba(255, 255, 255, 0.05),
      transparent);
  transform: rotate(45deg);
  transition: transform 0.6s ease;
}

.stat-card:hover::before {
  transform: rotate(45deg) translate(50%, 50%);
}

/* QR Code container */
.qr-container {
  position: relative;
}

.qr-container::before {
  content: '';
  position: absolute;
  inset: -4px;
  background: linear-gradient(45deg, #3B82F6, #8B5CF6);
  border-radius: inherit;
  z-index: -1;
  filter: blur(12px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.qr-container:hover::before {
  opacity: 0.6;
}

/* Mobile optimizations */
@media (max-width: 1024px) {
  .backdrop-blur-xl {
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }

  .text-shadow {
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
}

/* Print styles */
@media print {

  .bg-gradient-to-br,
  .bg-gradient-to-r,
  .bg-gradient-to-bl,
  .bg-gradient-to-tr {
    background: white !important;
    color: black !important;
  }

  .text-white {
    color: black !important;
  }

  .text-gray-300,
  .text-gray-400 {
    color: #666 !important;
  }

  .backdrop-blur-xl {
    backdrop-filter: none;
    background: rgba(0, 0, 0, 0.05) !important;
  }

  /* Hide animations in print */
  * {
    animation: none !important;
    transition: none !important;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .backdrop-blur-xl {
    background: rgba(0, 0, 0, 0.8) !important;
  }

  .text-gray-300,
  .text-gray-400 {
    color: #fff !important;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  * {
    animation: none !important;
    transition: none !important;
  }
}

/* Focus states for accessibility */
.focus\:ring-4:focus {
  outline: none;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.3);
}

/* Loading skeleton animation */
@keyframes skeleton {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }

  100% {
    opacity: 1;
  }
}

.skeleton {
  animation: skeleton 1.5s ease-in-out infinite;
}
</style>
